import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/
import { useOrgContext } from '@/hooks/useOrgContext';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Landmark, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { BankAccountCard } from '@/components/insurance/BankAccountCard';
import { BankAccountFormDialog } from '@/components/insurance/BankAccountFormDialog';
import type { IBankAccount } from '@/@types/invoice';

export function BankAccountsPage() {
  const { currentOrg, isLoading: orgLoading, isOrgOwner } = useOrgContext();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<IBankAccount | undefined>(undefined);

  const accounts = useQuery(
    api.bankAccounts.queries.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const removeAccount = useMutation(api.bankAccounts.mutations.remove);
  const setPrimary = useMutation(api.bankAccounts.mutations.setPrimary);

  const isLoading = orgLoading || accounts === undefined;

  const handleEdit = (account: IBankAccount) => {
    setEditingAccount(account);
    setIsFormOpen(true);
  };

  const handleDelete = async (accountId: IBankAccount['_id']) => {
    try {
      await removeAccount({ accountId });
      toast.success('Bank account deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete bank account');
    }
  };

  const handleSetPrimary = async (accountId: IBankAccount['_id']) => {
    try {
      await setPrimary({ accountId });
      toast.success('Primary account updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to set primary account');
    }
  };

  const handleOpenCreate = () => {
    setEditingAccount(undefined);
    setIsFormOpen(true);
  };

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Landmark className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage bank accounts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Bank Accounts</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading ? 'Loading...' : `${accounts?.length ?? 0} accounts`}
          </p>
        </div>
        {isOrgOwner && (
          <Button onClick={handleOpenCreate}>
            <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
            Add Bank Account
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-48 w-full rounded-xl" />
          ))}
        </div>
      )}

      {!isLoading && accounts && accounts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Landmark className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No bank accounts</h3>
          <p className="text-sm text-muted-foreground">
            Add a bank account to include payment details on invoices.
          </p>
        </div>
      )}

      {!isLoading && accounts && accounts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map(account => (
            <BankAccountCard
              key={account._id}
              account={account}
              isOrgOwner={isOrgOwner}
              onEdit={() => handleEdit(account)}
              onDelete={() => handleDelete(account._id)}
              onSetPrimary={() => handleSetPrimary(account._id)}
            />
          ))}
        </div>
      )}

      {currentOrg && (
        <BankAccountFormDialog
          open={isFormOpen}
          onOpenChange={setIsFormOpen}
          orgId={currentOrg._id}
          existingAccount={editingAccount}
        />
      )}
    </div>
  );
}
